import React from "react";
import { RotateCcw, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Message } from "@/store/zustand/supabaseStore";
import { useIsChatLoading } from "@/store/zustand/aiContextStore";
import { useRestoreMessage } from "./hooks/useRestoreMessage";

interface MessageRestoreButtonProps {
  message: Message;
}

const MessageRestoreButtonComponent: React.FC<MessageRestoreButtonProps> = ({
  message,
}) => {
  const isChatLoading = useIsChatLoading();
  const { restore, getMessageRestoreInfo } = useRestoreMessage();

  const {
    latestActivity,
    isCurrentHead,
    isOverwritten,
    isRestoring,
    canRestore,
  } = getMessageRestoreInfo(message.id);

  // Don't show button if:
  // - Chat is loading
  // - This is the current HEAD (already the active state)
  // - No activity exists and it's not overwritten (message has no code changes)
  if (isChatLoading || isCurrentHead || (!latestActivity && !isOverwritten)) {
    return null;
  }

  const restoreDisabled = !canRestore;

  let tooltipText = "Restore to this message";
  if (isRestoring) {
    tooltipText = "Restoring...";
  } else if (isOverwritten) {
    tooltipText = "Newer messages exist";
  } else if (!canRestore) {
    tooltipText = "Another restore in progress";
  }

  const buttonText = isRestoring
    ? "Restoring..."
    : isOverwritten
      ? "Overwritten"
      : "Restore";

  return (
    <button
      className={cn(
        "flex items-center gap-1 text-xs text-secondary-n-500 transition-colors",
        restoreDisabled
          ? "opacity-50 cursor-default"
          : "hover:text-secondary-n-300 cursor-pointer",
      )}
      onClick={() => restore(message.id)}
      data-tooltip-id="tooltip"
      data-tooltip-content={tooltipText}
      data-tooltip-place="top"
    >
      {isRestoring ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <RotateCcw className="w-3 h-3" />
      )}
      {buttonText}
    </button>
  );
};

export const MessageRestoreButton = React.memo(MessageRestoreButtonComponent);
